// src/media/format.ts
function formatTime(seconds) {
  if (!Number.isFinite(seconds)) return "0:00";
  const whole = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor(whole % 3600 / 60);
  const rest = whole % 60;
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}` : `${minutes}:${String(rest).padStart(2, "0")}`;
}

// src/media/use-playback.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
var PLAYING_EVENTS = ["genie:media-playing", "genie:audio-playing"];
var rangesOf = (element) => {
  const ranges = [];
  for (let i = 0; i < element.buffered.length; i += 1) ranges.push({ start: element.buffered.start(i), end: element.buffered.end(i) });
  return ranges;
};
function usePlayback({ exclusive = true, frameRate = 30, initialMuted = false, onFacts } = {}) {
  const media = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [live, setLive] = useState(false);
  const [buffered, setBuffered] = useState([]);
  const [volume, setVolumeState] = useState(1);
  const [muted, setMutedState] = useState(initialMuted);
  const [rate, setRateState] = useState(1);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [natural, setNatural] = useState(null);
  const facts2 = useRef(onFacts);
  facts2.current = onFacts;
  const readFacts = useCallback(() => {
    const element = media.current;
    if (!element) return;
    setLive(element.duration === Infinity);
    if (Number.isFinite(element.duration)) setDuration(element.duration);
    setReady(element.readyState >= 1);
    const width = element.videoWidth ?? 0, height = element.videoHeight ?? 0;
    if (width && height) setNatural({ width, height });
    if (element.readyState >= 1) facts2.current?.({ duration: Number.isFinite(element.duration) ? element.duration : 0, width, height });
  }, []);
  useEffect(() => {
    if ((media.current?.readyState ?? 0) >= 1) readFacts();
  }, [readFacts]);
  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    const tick = () => {
      if (media.current) setTime(media.current.currentTime);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing]);
  useEffect(() => {
    if (!exclusive) return;
    const others = (event) => {
      if (event.detail !== media.current) media.current?.pause();
    };
    for (const name of PLAYING_EVENTS) window.addEventListener(name, others);
    return () => {
      for (const name of PLAYING_EVENTS) window.removeEventListener(name, others);
    };
  }, [exclusive]);
  const play = useCallback(() => {
    const element = media.current;
    if (!element) return;
    void element.play().then(() => setBlocked(false)).catch((error) => {
      setPlaying(false);
      setBlocked(error instanceof Error && error.name === "NotAllowedError");
    });
  }, []);
  const pause = useCallback(() => media.current?.pause(), []);
  const toggle = useCallback(() => {
    if (media.current?.paused) play();
    else pause();
  }, [play, pause]);
  const seekTo = useCallback((seconds) => {
    const element = media.current;
    if (!element || !Number.isFinite(seconds)) return;
    const end = Number.isFinite(element.duration) ? element.duration : seconds;
    element.currentTime = Math.min(Math.max(seconds, 0), end);
    setTime(element.currentTime);
  }, []);
  const seekBy = useCallback((delta) => seekTo((media.current?.currentTime ?? 0) + delta), [seekTo]);
  const seekFraction = useCallback((fraction) => {
    const element = media.current;
    if (!element || !Number.isFinite(element.duration) || !element.duration) return;
    seekTo(Math.min(Math.max(fraction, 0), 1) * element.duration);
  }, [seekTo]);
  const stepFrame = useCallback((direction) => {
    media.current?.pause();
    seekBy(direction / Math.max(1, frameRate));
  }, [seekBy, frameRate]);
  const setMuted = useCallback((next) => {
    if (media.current) media.current.muted = next;
    setMutedState(next);
  }, []);
  const setVolume = useCallback((next) => {
    const value = Math.min(Math.max(next, 0), 1);
    if (media.current) {
      media.current.volume = value;
      media.current.muted = value === 0;
    }
    setVolumeState(value);
  }, []);
  const setRate = useCallback((next) => {
    if (media.current) media.current.playbackRate = next;
    setRateState(next);
  }, []);
  const bind = useMemo(() => ({
    ref: media,
    onPlay: () => {
      setPlaying(true);
      setBlocked(false);
      if (exclusive) for (const name of PLAYING_EVENTS) window.dispatchEvent(new CustomEvent(name, { detail: media.current }));
    },
    onPause: () => {
      setPlaying(false);
      if (media.current) setTime(media.current.currentTime);
    },
    onEnded: () => {
      setPlaying(false);
      if (media.current) setTime(media.current.currentTime);
    },
    onTimeUpdate: () => {
      if (media.current && media.current.paused) setTime(media.current.currentTime);
    },
    onDurationChange: readFacts,
    onLoadedMetadata: () => {
      setFailed(false);
      readFacts();
    },
    onProgress: () => {
      if (media.current) setBuffered(rangesOf(media.current));
    },
    onVolumeChange: () => {
      if (media.current) {
        setVolumeState(media.current.volume);
        setMutedState(media.current.muted);
      }
    },
    onRateChange: () => {
      if (media.current) setRateState(media.current.playbackRate);
    },
    onError: () => {
      setFailed(true);
      setPlaying(false);
    }
  }), [exclusive, readFacts]);
  return {
    playing,
    time,
    duration,
    live,
    buffered,
    volume,
    muted,
    rate,
    ready,
    failed,
    blocked,
    natural,
    toggle,
    play,
    pause,
    seekTo,
    seekBy,
    seekFraction,
    stepFrame,
    setMuted,
    setVolume,
    setRate,
    bind
  };
}

// src/media/use-media-facts.ts
import { useEffect as useEffect2, useState as useState2 } from "react";
var facts = /* @__PURE__ */ new Map();
var POSTER_AT = 0.1;
function probe(src) {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    const give = (poster) => resolve({ poster, duration: Number.isFinite(video.duration) ? video.duration : 0, width: video.videoWidth, height: video.videoHeight });
    const draw = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext("2d");
        if (!context || !canvas.width) return give(null);
        context.drawImage(video, 0, 0);
        give(canvas.toDataURL("image/jpeg", 0.72));
      } catch {
        give(null);
      }
    };
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(POSTER_AT, (video.duration || 1) / 2);
    };
    video.onseeked = draw;
    video.onerror = () => give(null);
    video.src = src;
  });
}
function mediaFacts(src) {
  let entry = facts.get(src);
  if (!entry) {
    entry = probe(src);
    facts.set(src, entry);
  }
  return entry;
}
function useMediaFacts(src) {
  const [result, setResult] = useState2(null);
  useEffect2(() => {
    if (!src) {
      setResult(null);
      return;
    }
    let live = true;
    setResult(null);
    void mediaFacts(src).then((value) => {
      if (live) setResult(value);
    });
    return () => {
      live = false;
    };
  }, [src]);
  return result;
}

// src/media/parts.tsx
import { useRef as useRef2, useState as useState3 } from "react";

// src/ui.ts
var cn = (...parts) => parts.filter(Boolean).join(" ");
var menuPanel = "rounded-lg border border-border bg-card p-1 shadow-xl";

// src/media/icons.tsx
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var svg = (size, children, stroke = false) => /* @__PURE__ */ jsx(
  "svg",
  {
    viewBox: "0 0 24 24",
    width: size,
    height: size,
    "aria-hidden": "true",
    fill: stroke ? "none" : "currentColor",
    stroke: stroke ? "currentColor" : void 0,
    strokeWidth: stroke ? 2 : void 0,
    strokeLinecap: stroke ? "round" : void 0,
    strokeLinejoin: stroke ? "round" : void 0,
    children
  }
);
var PlayIcon = ({ size = 16 }) => svg(size, /* @__PURE__ */ jsx("path", { d: "M8 5.5v13a1 1 0 0 0 1.53.85l10.4-6.5a1 1 0 0 0 0-1.7L9.53 4.65A1 1 0 0 0 8 5.5Z" }));
var PauseIcon = ({ size = 16 }) => svg(size, /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx("rect", { x: "6", y: "5", width: "4", height: "14", rx: "1.2" }),
  /* @__PURE__ */ jsx("rect", { x: "14", y: "5", width: "4", height: "14", rx: "1.2" })
] }));
var VolumeIcon = ({ size = 16 }) => svg(size, /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx("path", { d: "M11 5 6 9H3v6h3l5 4V5Z" }),
  /* @__PURE__ */ jsx("path", { d: "M15.5 8.5a5 5 0 0 1 0 7" }),
  /* @__PURE__ */ jsx("path", { d: "M18 6a8.5 8.5 0 0 1 0 12" })
] }), true);
var MuteIcon = ({ size = 16 }) => svg(size, /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx("path", { d: "M11 5 6 9H3v6h3l5 4V5Z" }),
  /* @__PURE__ */ jsx("path", { d: "m16 9 5 6M21 9l-5 6" })
] }), true);
var MaximizeIcon = ({ size = 16 }) => svg(size, /* @__PURE__ */ jsx("path", { d: "M8 3H3v5M16 3h5v5M16 21h5v-5M8 21H3v-5" }), true);
var MinimizeIcon = ({ size = 16 }) => svg(size, /* @__PURE__ */ jsx("path", { d: "M3 8h5V3M21 8h-5V3M21 16h-5v5M3 16h5v5" }), true);
var DownloadIcon = ({ size = 16 }) => svg(size, /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx("path", { d: "M12 3v12" }),
  /* @__PURE__ */ jsx("path", { d: "m7 11 5 5 5-5" }),
  /* @__PURE__ */ jsx("path", { d: "M4 20h16" })
] }), true);
var CloseIcon = ({ size = 16 }) => svg(size, /* @__PURE__ */ jsx("path", { d: "m5 5 14 14M19 5 5 19" }), true);
var RepeatIcon = ({ size = 16 }) => svg(size, /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx("path", { d: "M4 11V9a3 3 0 0 1 3-3h13" }),
  /* @__PURE__ */ jsx("path", { d: "m17 3 3 3-3 3" }),
  /* @__PURE__ */ jsx("path", { d: "M20 13v2a3 3 0 0 1-3 3H4" }),
  /* @__PURE__ */ jsx("path", { d: "m7 21-3-3 3-3" })
] }), true);
var PipIcon = ({ size = 16 }) => svg(size, /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx("rect", { x: "3", y: "5", width: "18", height: "14", rx: "2" }),
  /* @__PURE__ */ jsx("rect", { x: "12", y: "11", width: "7", height: "6", rx: "1" })
] }), true);
var SkipBackIcon = ({ size = 16 }) => svg(size, /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx("path", { d: "M11 6 4 12l7 6V6Z" }),
  /* @__PURE__ */ jsx("path", { d: "M20 6l-7 6 7 6V6Z" })
] }));
var SkipForwardIcon = ({ size = 16 }) => svg(size, /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx("path", { d: "m13 6 7 6-7 6V6Z" }),
  /* @__PURE__ */ jsx("path", { d: "M4 6l7 6-7 6V6Z" })
] }));
var StepBackIcon = ({ size = 16 }) => svg(size, /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx("path", { d: "M18 6 9 12l9 6V6Z" }),
  /* @__PURE__ */ jsx("rect", { x: "5", y: "6", width: "2", height: "12", rx: "1" })
] }));
var StepForwardIcon = ({ size = 16 }) => svg(size, /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx("path", { d: "m6 6 9 6-9 6V6Z" }),
  /* @__PURE__ */ jsx("rect", { x: "17", y: "6", width: "2", height: "12", rx: "1" })
] }));

// src/media/parts.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var stop = (event) => event.stopPropagation();
function MediaScrubber({ time, duration, label, buffered = [], onSeek, step = 2, bigStep = 10, size = "sm", children, className }) {
  const rail = useRef2(null);
  const scrubbing = useRef2(false);
  const [hover, setHover] = useState3(null);
  const progress = duration > 0 ? Math.min(1, time / duration) : 0;
  const fractionAt = (clientX) => {
    const rect = rail.current?.getBoundingClientRect();
    return rect?.width ? Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1) : 0;
  };
  const down = (event) => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    scrubbing.current = true;
    onSeek(fractionAt(event.clientX));
  };
  const move = (event) => {
    const fraction = fractionAt(event.clientX);
    setHover(fraction);
    if (scrubbing.current) onSeek(fraction);
  };
  const keys = (event) => {
    const jump = event.shiftKey ? bigStep : step;
    const next = event.key === "ArrowLeft" ? time - jump : event.key === "ArrowRight" ? time + jump : event.key === "Home" ? 0 : event.key === "End" ? duration : null;
    if (next === null) return;
    event.preventDefault();
    event.stopPropagation();
    if (duration > 0) onSeek(next / duration);
  };
  return /* @__PURE__ */ jsxs2(
    "div",
    {
      ref: rail,
      role: "slider",
      tabIndex: 0,
      "aria-label": label,
      "aria-valuemin": 0,
      "aria-valuemax": Math.round(duration * 100) / 100,
      "aria-valuenow": Math.round(time * 100) / 100,
      "aria-valuetext": `${formatTime(time)} of ${formatTime(duration)}`,
      onPointerDown: down,
      onPointerMove: move,
      onPointerUp: () => {
        scrubbing.current = false;
      },
      onPointerCancel: () => {
        scrubbing.current = false;
      },
      onPointerLeave: () => setHover(null),
      onKeyDown: keys,
      onClick: stop,
      className: cn(
        "nodrag nopan group/scrub relative flex min-w-0 flex-1 cursor-pointer touch-none items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring",
        size === "sm" ? "h-3" : "h-4",
        className
      ),
      children: [
        children ?? /* @__PURE__ */ jsxs2("span", { "aria-hidden": "true", className: cn("relative w-full overflow-hidden rounded-full bg-white/25", size === "sm" ? "h-1" : "h-1.5"), children: [
          buffered.map((range) => duration > 0 && /* @__PURE__ */ jsx2(
            "span",
            {
              className: "absolute inset-y-0 bg-white/40",
              style: { left: `${range.start / duration * 100}%`, width: `${(range.end - range.start) / duration * 100}%` }
            },
            `${range.start}-${range.end}`
          )),
          hover !== null && /* @__PURE__ */ jsx2("span", { className: "absolute inset-y-0 left-0 bg-white/50", style: { width: `${hover * 100}%` } }),
          /* @__PURE__ */ jsx2("span", { className: "absolute inset-y-0 left-0 bg-white", style: { width: `${progress * 100}%` } })
        ] }),
        /* @__PURE__ */ jsx2(
          "span",
          {
            "aria-hidden": "true",
            className: "pointer-events-none absolute top-1/2 h-[9px] w-[9px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white opacity-0 transition-opacity group-hover/scrub:opacity-100 group-focus-visible/scrub:opacity-100",
            style: { left: `${progress * 100}%` }
          }
        ),
        hover !== null && duration > 0 && /* @__PURE__ */ jsx2("span", { "aria-hidden": "true", className: "pointer-events-none absolute -top-5 -translate-x-1/2 rounded bg-white px-1 py-px text-[9px] tabular-nums text-black", style: { left: `${hover * 100}%` }, children: formatTime(hover * duration) })
      ]
    }
  );
}
var BUTTON = "nodrag flex shrink-0 cursor-pointer items-center justify-center rounded-full transition-transform hover:scale-105 active:scale-95 disabled:cursor-default disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";
function PlayButton({ playing, onToggle, label, size = "md", disabled }) {
  const box = size === "lg" ? "h-10 w-10" : size === "sm" ? "h-5 w-5" : "h-6 w-6";
  const glyph = size === "lg" ? 18 : size === "sm" ? 9 : 11;
  return /* @__PURE__ */ jsx2(
    "button",
    {
      type: "button",
      "aria-label": label,
      disabled,
      onClick: (event) => {
        stop(event);
        onToggle();
      },
      onPointerDown: stop,
      className: cn(BUTTON, box, "bg-white text-black"),
      children: playing ? /* @__PURE__ */ jsx2(PauseIcon, { size: glyph }) : /* @__PURE__ */ jsx2("span", { className: "translate-x-px", children: /* @__PURE__ */ jsx2(PlayIcon, { size: glyph }) })
    }
  );
}
function IconButton({ icon, label, pressed, onPress }) {
  return /* @__PURE__ */ jsx2(
    "button",
    {
      type: "button",
      "aria-label": label,
      "aria-pressed": pressed,
      onClick: (event) => {
        stop(event);
        onPress();
      },
      onPointerDown: stop,
      className: cn(BUTTON, "h-5 w-5", pressed ? "text-white" : "text-white/70 hover:text-white"),
      children: icon
    }
  );
}
function MuteButton({ muted, onToggle, label }) {
  return /* @__PURE__ */ jsx2(IconButton, { icon: muted ? /* @__PURE__ */ jsx2(MuteIcon, { size: 13 }) : /* @__PURE__ */ jsx2(VolumeIcon, { size: 13 }), label, pressed: muted, onPress: onToggle });
}
function TimeReadout({ time, duration }) {
  return /* @__PURE__ */ jsxs2("span", { className: "shrink-0 text-[9px] tabular-nums text-white/80", children: [
    formatTime(time),
    " / ",
    formatTime(duration)
  ] });
}
function ControlBar({ reveal, visible = false, children }) {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      onPointerDown: stop,
      onDoubleClick: stop,
      className: cn(
        "nodrag absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/70 to-transparent px-2 pb-1.5 pt-4 transition-opacity",
        reveal === "always" || visible ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
      ),
      children
    }
  );
}

// src/media/VideoPlayer.tsx
import { memo, useCallback as useCallback2, useEffect as useEffect3, useLayoutEffect, useRef as useRef3, useState as useState4 } from "react";
import { Fragment as Fragment2, jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];
var SKIP_SECONDS = 5;
var MICRO_WIDTH = 120;
var FULL_WIDTH = 480;
var TIGHT_WIDTH = 180;
var VideoPlayer = memo(function VideoPlayer2({
  src,
  title = "Video",
  poster,
  gradient,
  density = "auto",
  barPinned = false,
  defer = false,
  loop = false,
  startMuted = true,
  hoverPreview = false,
  fps = 30,
  objectFit = "contain",
  crossOrigin,
  durationHint,
  onOpenLarge,
  onDownload,
  onSize,
  time: controlledTime,
  onTime,
  className
}) {
  const root = useRef3(null);
  const [width, setWidth] = useState4(0);
  const [speedOpen, setSpeedOpen] = useState4(false);
  const [looping, setLooping] = useState4(loop);
  const [armed, setArmed] = useState4(!poster && !defer);
  const [autoPlay, setAutoPlay] = useState4(false);
  const playback = usePlayback({ frameRate: fps, initialMuted: startMuted, onFacts: (facts2) => {
    if (facts2.width) onSize?.(facts2.width, facts2.height);
  } });
  const { playing, time, duration, buffered, muted, rate, failed, blocked, bind, play, pause, toggle, seekBy, seekFraction, seekTo, stepFrame, setMuted, setRate } = playback;
  useEffect3(() => {
    setAutoPlay(false);
    setArmed(!poster && !defer);
  }, [poster, defer, src]);
  useEffect3(() => {
    onTime?.(time);
  }, [time, onTime]);
  const driven = useRef3(controlledTime);
  useEffect3(() => {
    if (controlledTime === void 0 || controlledTime === driven.current) return;
    driven.current = controlledTime;
    seekTo(controlledTime);
  }, [controlledTime, seekTo]);
  useEffect3(() => {
    if (blocked && !muted) {
      setMuted(true);
      play();
    }
  }, [blocked, muted, setMuted, play]);
  useLayoutEffect(() => {
    const element = root.current;
    if (!element || density !== "auto") return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0));
    observer.observe(element);
    return () => observer.disconnect();
  }, [density]);
  const tier = density !== "auto" ? density : width && width < MICRO_WIDTH ? "micro" : width > FULL_WIDTH ? "full" : "compact";
  const tight = tier !== "full" && width > 0 && width < TIGHT_WIDTH;
  const start = useCallback2(() => {
    if (armed) {
      toggle();
      return;
    }
    setAutoPlay(true);
    setArmed(true);
  }, [armed, toggle]);
  const keys = (event) => {
    if (event.key !== " " && event.key !== "k") return;
    event.preventDefault();
    event.stopPropagation();
    start();
  };
  const hover = (entering) => {
    if (!hoverPreview) return;
    if (!entering) {
      pause();
      return;
    }
    setMuted(true);
    if (armed) play();
    else {
      setAutoPlay(true);
      setArmed(true);
    }
  };
  const stop2 = (event) => event.stopPropagation();
  if (failed) return /* @__PURE__ */ jsx3("div", { role: "alert", className: cn("flex h-full w-full items-center justify-center bg-card p-3 text-xs text-muted-foreground", className), children: "This clip couldn\u2019t be loaded" });
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      ref: root,
      "data-genie-player": true,
      className: cn("nokey group relative h-full w-full overflow-hidden bg-black", className),
      style: !armed && gradient ? { background: gradient } : void 0,
      onKeyDown: keys,
      onPointerEnter: () => hover(true),
      onPointerLeave: () => hover(false),
      children: [
        armed ? /* @__PURE__ */ jsx3(
          "video",
          {
            src,
            ...bind,
            loop: looping,
            muted,
            autoPlay,
            playsInline: true,
            preload: "metadata",
            crossOrigin,
            className: cn("h-full w-full", objectFit === "cover" ? "object-cover" : "object-contain")
          }
        ) : /* @__PURE__ */ jsxs3(Fragment2, { children: [
          poster && /* @__PURE__ */ jsx3("img", { src: poster, alt: "", draggable: false, className: cn("h-full w-full", objectFit === "cover" ? "object-cover" : "object-contain") }),
          /* @__PURE__ */ jsx3(
            "button",
            {
              type: "button",
              "aria-label": `Play ${title}`,
              onPointerDown: stop2,
              onClick: (event) => {
                event.stopPropagation();
                start();
              },
              className: "nodrag absolute inset-0 flex cursor-pointer items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
              children: /* @__PURE__ */ jsx3("span", { className: "flex aspect-square h-[18%] min-h-6 max-h-11 items-center justify-center rounded-full bg-white/85 text-black opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 group-focus-within:opacity-100", children: /* @__PURE__ */ jsx3("span", { className: "translate-x-px", children: /* @__PURE__ */ jsx3(PlayIcon, { size: 16 }) }) })
            }
          ),
          (durationHint ?? duration) > 0 && /* @__PURE__ */ jsx3("span", { "aria-hidden": "true", className: "pointer-events-none absolute bottom-1.5 right-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[9px] tabular-nums text-white/80", children: formatTime(durationHint ?? duration) })
        ] }),
        armed && tier !== "micro" && /* @__PURE__ */ jsxs3(ControlBar, { reveal: barPinned ? "always" : "hover-or-playing", visible: playing || speedOpen, children: [
          /* @__PURE__ */ jsx3(PlayButton, { playing, onToggle: toggle, label: playing ? `Pause ${title}` : `Play ${title}` }),
          tier === "full" && /* @__PURE__ */ jsxs3(Fragment2, { children: [
            /* @__PURE__ */ jsx3(IconButton, { icon: /* @__PURE__ */ jsx3(SkipBackIcon, { size: 12 }), label: `Back ${SKIP_SECONDS} seconds`, onPress: () => seekBy(-SKIP_SECONDS) }),
            /* @__PURE__ */ jsx3(IconButton, { icon: /* @__PURE__ */ jsx3(StepBackIcon, { size: 12 }), label: "Previous frame", onPress: () => stepFrame(-1) }),
            /* @__PURE__ */ jsx3(IconButton, { icon: /* @__PURE__ */ jsx3(StepForwardIcon, { size: 12 }), label: "Next frame", onPress: () => stepFrame(1) }),
            /* @__PURE__ */ jsx3(IconButton, { icon: /* @__PURE__ */ jsx3(SkipForwardIcon, { size: 12 }), label: `Forward ${SKIP_SECONDS} seconds`, onPress: () => seekBy(SKIP_SECONDS) })
          ] }),
          /* @__PURE__ */ jsx3(
            MediaScrubber,
            {
              time,
              duration,
              buffered,
              onSeek: seekFraction,
              label: `Seek ${title}`,
              step: 1 / fps,
              bigStep: 10 / fps
            }
          ),
          !tight && /* @__PURE__ */ jsx3(TimeReadout, { time, duration }),
          tier === "full" && /* @__PURE__ */ jsxs3("span", { className: "relative shrink-0", children: [
            /* @__PURE__ */ jsxs3(
              "button",
              {
                type: "button",
                "aria-label": "Playback speed",
                "aria-expanded": speedOpen,
                onPointerDown: stop2,
                onClick: (event) => {
                  event.stopPropagation();
                  setSpeedOpen((open) => !open);
                },
                className: "nodrag cursor-pointer rounded px-1 text-[9px] tabular-nums text-white/70 transition-colors hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                children: [
                  rate,
                  "\xD7"
                ]
              }
            ),
            speedOpen && /* @__PURE__ */ jsx3("span", { className: "absolute bottom-6 right-0 z-10 flex flex-col overflow-hidden rounded bg-black/90 py-0.5 text-[9px] text-white/80", children: SPEEDS.map((speed) => /* @__PURE__ */ jsxs3(
              "button",
              {
                type: "button",
                onPointerDown: stop2,
                onClick: (event) => {
                  event.stopPropagation();
                  setRate(speed);
                  setSpeedOpen(false);
                },
                className: cn("nodrag cursor-pointer px-2 py-1 text-right tabular-nums hover:bg-white/15", speed === rate && "text-white"),
                children: [
                  speed,
                  "\xD7"
                ]
              },
              speed
            )) })
          ] }),
          tier === "full" && /* @__PURE__ */ jsx3(IconButton, { icon: /* @__PURE__ */ jsx3(RepeatIcon, { size: 12 }), label: "Loop", pressed: looping, onPress: () => setLooping((value) => !value) }),
          !tight && /* @__PURE__ */ jsx3(MuteButton, { muted, onToggle: () => setMuted(!muted), label: muted ? `Unmute ${title}` : `Mute ${title}` }),
          onDownload && tier === "full" && /* @__PURE__ */ jsx3(IconButton, { icon: /* @__PURE__ */ jsx3(DownloadIcon, { size: 12 }), label: `Download ${title}`, onPress: onDownload }),
          onOpenLarge && /* @__PURE__ */ jsx3(IconButton, { icon: /* @__PURE__ */ jsx3(MaximizeIcon, { size: 12 }), label: `Open ${title} large`, onPress: onOpenLarge })
        ] })
      ]
    }
  );
});

// src/media/VideoLightbox.tsx
import { useCallback as useCallback3, useEffect as useEffect4, useRef as useRef4, useState as useState5 } from "react";
import { createPortal } from "react-dom";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var IDLE_MS = 2500;
function VideoLightbox({ src, title = "Video", open, onClose, onDownload }) {
  const wrapper = useRef4(null);
  const [idle, setIdle] = useState5(false);
  const [full, setFull] = useState5(false);
  const [size, setSize] = useState5(null);
  const wake = useCallback3(() => {
    setIdle(false);
    const timer = window.setTimeout(() => setIdle(true), IDLE_MS);
    return () => window.clearTimeout(timer);
  }, []);
  useEffect4(() => {
    if (open) return wake();
  }, [open, wake]);
  useEffect4(() => {
    if (!open) return;
    const onFullscreen = () => setFull(document.fullscreenElement === wrapper.current);
    document.addEventListener("fullscreenchange", onFullscreen);
    return () => document.removeEventListener("fullscreenchange", onFullscreen);
  }, [open]);
  useEffect4(() => {
    if (!open) return;
    const previous = document.activeElement;
    wrapper.current?.focus();
    const keys = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = wrapper.current?.querySelectorAll('button, [role="slider"], [href], input');
      if (!focusable?.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", keys);
    return () => {
      window.removeEventListener("keydown", keys);
      previous?.focus();
    };
  }, [open, onClose]);
  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void wrapper.current?.requestFullscreen().catch(() => {
    });
  };
  const pip = () => {
    const video = wrapper.current?.querySelector("video");
    if (!video) return;
    if (document.pictureInPictureElement) void document.exitPictureInPicture();
    else void video.requestPictureInPicture().catch(() => {
    });
  };
  if (!open) return null;
  const chrome = idle ? "opacity-0" : "opacity-100";
  return createPortal(
    /* @__PURE__ */ jsxs4(
      "div",
      {
        ref: wrapper,
        role: "dialog",
        "aria-modal": "true",
        "aria-label": title,
        tabIndex: -1,
        onPointerMove: wake,
        onPointerDown: (event) => {
          if (event.target === event.currentTarget) onClose();
        },
        className: "fixed inset-0 z-50 flex flex-col bg-black/95 outline-none",
        children: [
          /* @__PURE__ */ jsxs4("header", { className: `flex shrink-0 items-center gap-3 px-4 py-3 text-xs text-white/70 transition-opacity ${chrome}`, children: [
            /* @__PURE__ */ jsx4("span", { className: "min-w-0 truncate text-white", children: title }),
            size && /* @__PURE__ */ jsxs4("span", { className: "shrink-0 tabular-nums text-white/50", children: [
              size.width,
              "\xD7",
              size.height
            ] }),
            /* @__PURE__ */ jsxs4("span", { className: "ml-auto flex shrink-0 items-center gap-1", children: [
              onDownload && /* @__PURE__ */ jsx4("button", { type: "button", "aria-label": `Download ${title}`, onClick: onDownload, className: "cursor-pointer rounded p-1.5 text-white/70 hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring", children: /* @__PURE__ */ jsx4(DownloadIcon, { size: 15 }) }),
              document.pictureInPictureEnabled && /* @__PURE__ */ jsx4("button", { type: "button", "aria-label": "Picture in picture", onClick: pip, className: "cursor-pointer rounded p-1.5 text-white/70 hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring", children: /* @__PURE__ */ jsx4(PipIcon, { size: 15 }) }),
              /* @__PURE__ */ jsx4("button", { type: "button", "aria-label": full ? "Leave fullscreen" : "Fullscreen", onClick: toggleFullscreen, className: "cursor-pointer rounded p-1.5 text-white/70 hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring", children: full ? /* @__PURE__ */ jsx4(MinimizeIcon, { size: 15 }) : /* @__PURE__ */ jsx4(MaximizeIcon, { size: 15 }) }),
              /* @__PURE__ */ jsx4("button", { type: "button", "aria-label": "Close", onClick: onClose, className: "cursor-pointer rounded p-1.5 text-white/70 hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring", children: /* @__PURE__ */ jsx4(CloseIcon, { size: 15 }) })
            ] })
          ] }),
          /* @__PURE__ */ jsx4("div", { className: "min-h-0 flex-1 px-4 pb-4", children: /* @__PURE__ */ jsx4(
            VideoPlayer,
            {
              src,
              title,
              density: "full",
              barPinned: true,
              startMuted: false,
              onDownload,
              onSize: (width, height) => setSize({ width, height }),
              className: "bg-transparent"
            }
          ) })
        ]
      }
    ),
    document.body
  );
}

export {
  cn,
  menuPanel,
  formatTime,
  usePlayback,
  mediaFacts,
  useMediaFacts,
  MediaScrubber,
  PlayButton,
  IconButton,
  MuteButton,
  TimeReadout,
  ControlBar,
  VideoPlayer,
  VideoLightbox
};
//# sourceMappingURL=chunk-GZNI65HQ.js.map